import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import { Button } from "@/components/ui/button";
import { AdminBlogEditor } from "@/components/AdminBlogEditor";
import { Plus, Pencil, Trash2, Eye, EyeOff, Tag } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export default function AdminBlog() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [managingTags, setManagingTags] = useState(false);
  const [newTagName, setNewTagName] = useState('');

  // Check admin
  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/auth'); return; }
      const { data } = await supabase.rpc('has_role', { _user_id: session.user.id, _role: 'admin' });
      if (!data) { navigate('/'); return; }
      setIsAdmin(true);
      setIsChecking(false);
    };
    checkAdmin();
  }, [navigate]);

  // Fetch all posts (admin sees all)
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['admin-blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: isAdmin,
  });

  // Fetch all tags
  const { data: tags = [] } = useQuery({
    queryKey: ['blog-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_tags')
        .select('*')
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: isAdmin,
  });

  // Delete post
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('blog_posts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      toast({ title: 'הפוסט נמחק בהצלחה' });
    },
  });

  // Toggle publish
  const togglePublishMutation = useMutation({
    mutationFn: async ({ id, isPublished }: { id: string; isPublished: boolean }) => {
      const update: any = { is_published: !isPublished };
      if (!isPublished) update.published_at = new Date().toISOString();
      const { error } = await supabase.from('blog_posts').update(update).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      toast({ title: 'הסטטוס עודכן' });
    },
  });

  // Create tag
  const createTagMutation = useMutation({
    mutationFn: async (name: string) => {
      const slug = name.trim().replace(/\s+/g, '-');
      const { error } = await supabase.from('blog_tags').insert({ name: name.trim(), slug });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-tags'] });
      setNewTagName('');
      toast({ title: 'תג נוצר בהצלחה' });
    },
  });

  // Delete tag
  const deleteTagMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('blog_tags').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-tags'] });
      toast({ title: 'תג נמחק' });
    },
  });

  if (isChecking || !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" dir="rtl">
        <div className="gold-bar-loader" />
      </div>
    );
  }

  // Show editor
  if (isCreating || editingPostId) {
    return (
      <div className="min-h-screen bg-background" dir="rtl">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdminBlogEditor
            postId={editingPostId}
            onClose={() => {
              setEditingPostId(null);
              setIsCreating(false);
              queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
            }}
          />
        </div>
        <Footer2 />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-foreground">ניהול בלוג</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setManagingTags(!managingTags)}
              className="gap-2"
            >
              <Tag className="h-4 w-4" />
              ניהול תגים
            </Button>
            <Button onClick={() => setIsCreating(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              פוסט חדש
            </Button>
          </div>
        </div>

        {/* Tags Management */}
        {managingTags && (
          <div className="border border-border p-4 mb-8 bg-card">
            <h2 className="text-lg font-semibold mb-4">ניהול תגים</h2>
            <div className="flex gap-2 mb-4">
              <input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="שם תג חדש..."
                className="flex-1 border border-border bg-background px-3 py-2 text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTagName.trim()) {
                    createTagMutation.mutate(newTagName);
                  }
                }}
              />
              <Button
                size="sm"
                onClick={() => newTagName.trim() && createTagMutation.mutate(newTagName)}
                disabled={!newTagName.trim()}
              >
                הוסף
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map(tag => (
                <div key={tag.id} className="flex items-center gap-1 border border-border px-2 py-1 text-sm">
                  <span>{tag.name}</span>
                  <button
                    onClick={() => {
                      if (confirm(`למחוק את התג "${tag.name}"?`)) {
                        deleteTagMutation.mutate(tag.id);
                      }
                    }}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Posts List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p>אין פוסטים עדיין. צור את הפוסט הראשון!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map(post => (
              <div
                key={post.id}
                className="flex items-center gap-4 border border-border p-4 bg-card hover:bg-accent/5 transition-colors"
              >
                {/* Thumbnail */}
                <div className="w-16 h-16 flex-shrink-0 bg-muted overflow-hidden">
                  {post.cover_image_url ? (
                    <img src={post.cover_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">—</div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground truncate">{post.title}</h3>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                    <span className={post.is_published ? 'text-green-600' : 'text-yellow-600'}>
                      {post.is_published ? 'מפורסם' : 'טיוטה'}
                    </span>
                    <span>•</span>
                    <span>{new Date(post.created_at).toLocaleDateString('he-IL')}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => togglePublishMutation.mutate({ id: post.id, isPublished: post.is_published })}
                    title={post.is_published ? 'הסתר' : 'פרסם'}
                  >
                    {post.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingPostId(post.id)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm('למחוק את הפוסט?')) {
                        deleteMutation.mutate(post.id);
                      }
                    }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer2 />
    </div>
  );
}
